import { Grid2 as Grid, Link, Typography, Tooltip, Box, Button, useTheme } from "@mui/material";
import React, { useMemo } from "react";
import { InvoiceDetailsModel, InvoiceStatus, UserInfoModel } from "../../../models/ChasableModels";
import { calculateOverdueColorHex } from "../../../services/helpers";
import OverdueCalculationComponent from "../../../components/OverdueCalculationComponent";
import { TableSection } from "../../../components/TableSection";
import { environment } from "../../../config/environment";
import { ExternalLink } from "../../../components/ExternalLink";
import { NewTag } from "../../../components/NewTag";
import { useInvoiceDispute } from "../../../hooks/useInvoiceDispute";
import InvoiceDisputeDialog from "../../../components/InvoiceDisputeDialog";
import InvoiceDisputeReasonDialog from "../../../components/InvoiceDisputeReasonDialog";
import { formatDate } from "../../../utils/formatDate";
import useFeatureToggles from "../../../hooks/useFeatureToggles";
import { PromiseToPayBrokenTag } from "../../../components/PromiseToPayBrokenTag";
import { EditDateButton } from "../../../components/EditDateButton";
import { useUpdateChasable } from "../../../hooks/useUpdateChasable";

type Props = {
  invoice: InvoiceDetailsModel;
  companyName: string;
  accountNumber?: string;
  assignedTo: UserInfoModel | null | undefined;
  customerLink?: string;
  isNewInvoice: boolean;
  isPromiseToPayBroken: boolean;
  onRefresh?: () => void;
};

export function InvoiceInformation({
  invoice,
  companyName,
  accountNumber,
  assignedTo,
  customerLink,
  isNewInvoice,
  isPromiseToPayBroken,
  onRefresh,
}: Props) {
  const { isDisputedEnabled, isPromiseToPayEditEnabled } = useFeatureToggles();

  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  const [openDisputeDialog, setDisputeDialog] = React.useState(false);
  const [openDisputeReasonDialog, setDisputeReasonDialog] = React.useState(false);

  const { disputeReasonCodes, isInvoiceDisputeSubmitting, submitInvoiceDispute } = useInvoiceDispute(isDisputedEnabled);

  const tableBgColor = useMemo(() => {
    if (!isDisputedEnabled) return;
    if (!invoice.dispute) return;
    return isDarkMode ? "#5c1a1a" : "#ffe6e6";
  }, [isDarkMode, invoice.dispute, isDisputedEnabled]);

  const tableDefinition = useMemo(() => {
    const definition = [
      {
        title: "Assigned To",
        value: (
          <AssignedTo assignedTo={assignedTo} invoiceId={invoice.id} isDraft={invoice.status === InvoiceStatus.DRAFT} />
        ),
      },
      {
        title: "Action",
        value: "Overdue invoice",
      },
      {
        title: "Invoice Amount",
        value: Intl.NumberFormat(Office.context.displayLanguage, {
          currency: invoice.currency ?? "GBP",
          style: "currency",
          currencyDisplay: "code",
        }).format(invoice.amount),
      },
      {
        title: "Remaining Amount",
        value: Intl.NumberFormat(Office.context.displayLanguage, {
          currency: invoice.currency ?? "GBP",
          style: "currency",
          currencyDisplay: "code",
        }).format(invoice.remainingAmount),
      },
      {
        title: "Account Number",
        value: accountNumber,
      },
      {
        title: "Customer",
        value: <ExternalLink href={customerLink}>{companyName}</ExternalLink>,
      },
      {
        title: "Invoice",
        value: (
          <Grid container={true} alignItems="center" justifyContent="flex-end">
            {isNewInvoice && <NewTag sx={{ mr: 1 }} />}
            {isPromiseToPayBroken && <PromiseToPayBrokenTag sx={{ mr: 1 }} />}
            <ExternalLink href={invoice.link}>{invoice.invoiceNumber}</ExternalLink>
          </Grid>
        ),
      },
      {
        title: "Invoice Date",
        value: formatDate(invoice.invoiceDate),
      },
      {
        title: "Due Date",
        value: <DueDate {...invoice} />,
      },
    ];

    if (isPromiseToPayEditEnabled) {
      definition.push({
        title: "Promise to Pay",
        value: <PromiseToPayDate invoiceId={invoice.id} date={invoice.plannedDate} onUpdated={onRefresh} />,
      });
    }

    if (isDisputedEnabled) {
      if (invoice.dispute) {
        const maxLength = 50;
        const reason = invoice.dispute.reason || "";
        // Make the displayed text clearer by stripping any HTML tags
        const cleanReason = reason.replace(/<[^>]+>/g, "");
        const hasLongReason = cleanReason.length > maxLength;
        const shortReason = hasLongReason ? cleanReason.slice(0, maxLength) + "..." : cleanReason;
        definition.push({
          title: "Disputed",
          value: (
            <Typography
              variant="body2"
              sx={{
                cursor: "pointer",
                display: "inline-block",
                textAlign: "left",
                textDecoration: "underline",
                whiteSpace: "pre-wrap",
                wordBreak: "break-all",
                px: 1,
                py: 0.5,
                borderRadius: 1,
                bgcolor: (theme) =>
                  theme.palette.mode === "dark" ? theme.palette.error.dark : theme.palette.error.main,
                color: (theme) =>
                  theme.palette.getContrastText(
                    theme.palette.mode === "dark" ? theme.palette.error.dark : theme.palette.error.main
                  ),
              }}
              onClick={() => {
                setDisputeReasonDialog(true);
              }}
            >
              {shortReason}
            </Typography>
          ),
        });
      } else {
        definition.push({
          title: "Dispute",
          value: (
            <Button
              variant="contained"
              color="error"
              size="small"
              onClick={() => {
                setDisputeDialog(true);
              }}
            >
              Start Dispute
            </Button>
          ),
        });
      }
    }

    return definition;
  }, [
    assignedTo,
    invoice,
    companyName,
    customerLink,
    isNewInvoice,
    isDisputedEnabled,
    isPromiseToPayEditEnabled,
    accountNumber,
    isPromiseToPayBroken,
    onRefresh,
  ]);

  return (
    <Box>
      <TableSection
        title="Information"
        ariaLabel="chasable table"
        tableDefinition={tableDefinition}
        tableSx={{ background: tableBgColor }}
      />

      <InvoiceDisputeDialog
        open={openDisputeDialog}
        codes={disputeReasonCodes}
        isLoading={isInvoiceDisputeSubmitting}
        onClose={() => {
          setDisputeDialog(false);
        }}
        onSubmit={async (value) => {
          await submitInvoiceDispute({
            invoiceId: invoice.id,
            reason: value.reason,
            reasonCode: value.reasonCode,
          });
          setDisputeDialog(false);
          onRefresh?.();
        }}
      />

      <InvoiceDisputeReasonDialog
        open={openDisputeReasonDialog}
        reason={invoice.dispute?.reason ?? ""}
        onClose={() => {
          setDisputeReasonDialog(false);
        }}
      />
    </Box>
  );
}

function AssignedTo({
  assignedTo,
  invoiceId,
  isDraft,
}: {
  assignedTo: UserInfoModel | null | undefined;
  invoiceId: string;
  isDraft: boolean;
}) {
  if (isDraft) {
    return <Typography>N/A</Typography>;
  }

  if (!assignedTo)
    return (
      <Link href={`${environment.webAppUrl}/actions?action=${invoiceId}`} target="_blank">
        Assign in app
      </Link>
    );

  return assignedTo.fullName;
}

function PromiseToPayDate({
  invoiceId,
  date,
  onUpdated,
}: {
  invoiceId: string;
  date?: string | null;
  onUpdated?: () => void;
}) {
  const { updateChasable } = useUpdateChasable(invoiceId);

  return (
    <Grid container={true} alignItems="center" justifyContent="end" flexWrap="nowrap">
      <Grid mr={0.5}>{date ? formatDate(date) : "N/A"}</Grid>
      <EditDateButton
        date={date}
        title="Select a promise to pay date"
        onSave={async (plannedDate) => {
          await updateChasable({ plannedDate });
          onUpdated?.();
        }}
      />
    </Grid>
  );
}

function DueDate(props: InvoiceDetailsModel) {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  if (props.status === InvoiceStatus.PAID || props.status === InvoiceStatus.DRAFT) {
    return formatDate(props.dueDate);
  }

  return (
    <Tooltip
      title={
        <OverdueCalculationComponent
          component="span"
          dueDate={new Date(props.dueDate)}
          color={isDarkMode ? undefined : calculateOverdueColorHex(new Date(props.dueDate))}
        />
      }
      arrow
      placement="top"
    >
      <span>{formatDate(props.dueDate)}</span>
    </Tooltip>
  );
}
